import { CheckCircleFilled, CloseCircleOutlined, InfoCircleFilled, StarFilled } from '@ant-design/icons';
import { App } from '@capacitor/app';
import {
  Purchases,
  PURCHASES_ERROR_CODE,
  PurchasesOffering,
  PurchasesPackage,
  SubscriptionOption,
} from '@revenuecat/purchases-capacitor';
import { useRequest } from 'ahooks';
import { Badge, Carousel, Collapse, Input, Modal } from 'antd';
import IconRound from 'assets/logos/icon-round.svg?react';
import CaregiverAvatarList from 'components/lists/CaregiverAvatarList';
import { Button } from 'components/ui';
import { colors, shadows } from 'design';
import usePreferences from 'hooks/usePreferences';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useDispatch } from 'react-redux';
import useRefreshCurrentUser from 'hooks/useRefreshCurrentUser';
import { Link } from 'react-router-dom';
import { userActions } from 'store/users';
import Platform from 'utils/Platform';

const useStyles = createUseStyles({
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 32,
    marginBottom: '20px',
  },
  title: {
    textAlign: 'center',
  },
  advantages: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    marginTop: 32,
    marginBottom: 16,
    '& p': {
      margin: 4,
      textAlign: 'center',
    },
    '& svg': {
      color: colors.brand.accentStrong,
    },
  },
  exceptSundays: {
    color: colors.text.disabled,
    fontSize: '0.9em',
  },
  advantagesList: {
    display: 'flex',
    flexDirection: 'row',
    gap: 32,
    justifyContent: 'center',
    color: colors.text.tertiary,

    '& svg': {
      marginRight: 8,
    },
  },
  whyIsItNotFree: {
    marginTop: 24,
    marginBottom: 16,
    textAlign: 'center',
    borderRadius: 12,
    padding: 12,

    '& svg': {
      marginRight: 4,
    },

    '& .ant-collapse-header': {
      padding: '0',
      color: colors.text.tertiary,
    },

    '& .ant-collapse-content-box': {
      padding: '0',
      color: colors.text.tertiary,
    },
  },
  testimonial: {
    marginTop: 48,
    textAlign: 'center',
    '& .anticon': {
      color: colors.dataViz.average,
    },

    '& p': {
      marginTop: 4,
      marginBottom: 16,
      fontStyle: 'italic',
    },
  },
  testimonialAuthor: {
    color: colors.text.tertiary,
  },
  pricingOption: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    borderRadius: '10px',
    margin: '20px 0px',
    boxShadow: shadows.xs,
    backgroundColor: colors.surface.page,
    border: `2px solid ${colors.surface.page}`,

    '& p': {
      margin: 0,
    },
  },
  selectedPricingOption: {
    composes: '$pricingOption',
    backgroundColor: colors.brand.accentSubtle,
    boxShadow: shadows.sm,
    border: `2px solid ${colors.brand.accent}`,
  },
  pricingOptionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    textAlign: 'left',
  },
  pricingOptionSubtitle: {
    fontSize: '15px',
  },
  pricingOptionPrice: {
    fontSize: '15px',
    textAlign: 'right',
  },
  secondPricePhase: {
    fontSize: '10px',
    marginTop: 4,
    color: colors.text.tertiary,
  },
  conditions: {
    fontSize: '0.9em',
    color: colors.text.tertiary,
    textAlign: 'center',
  },
  promoCodeContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0px 32px',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  promoCodeStatus: {
    display: 'flex',
    textAlign: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
  },
  paymentSuccessTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      marginLeft: 16,
      color: colors.brand.accentStrong,
    },
  },
  paymentFailedTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      marginRight: 12,
      color: colors.dataViz.veryBad,
    },
  },
  paymentDescription: {
    fontSize: '1em',
    color: colors.text.tertiary,
    textAlign: 'center',
    margin: '32px 0px',
  },
  legal: {
    textAlign: 'center',
    fontSize: '0.8em',
    color: colors.text.tertiary,
    marginTop: '20px',
    '& a': {
      color: colors.text.tertiary,
    },
  },
});

interface PaywallModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onClose: () => void;
  onFreeMessagesEnabled?: () => void;
  suggestTestIfEligible?: boolean;
}

const PaywallModal: React.FC<PaywallModalProps> = ({
  isOpen,
  onClose,
  closeModal,
  onFreeMessagesEnabled,
  suggestTestIfEligible = true,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [displayPage, setDisplayPage] = useState<
    'offers' | 'paymentSucceeded' | 'paymentFailed' | 'freeMessagesEnabled'
  >('offers');
  const [purchaseInProgress, setPurchaseInProgress] = useState(false);
  const dispatch = useDispatch();
  const refreshCurrentUser = useRefreshCurrentUser();
  const messagesLimit = 5;

  const [offering, setOffering] = useState<PurchasesOffering | null>(null);

  const { run: getOfferings } = useRequest(() => Purchases.getOfferings(), {
    manual: true,
    onSuccess: offerings => {
      setOffering(offerings.all['offering2']);
    },
  });
  const { value: didEnableFreeMessagesValue } = usePreferences(Keys.PAYWALL_DID_ENABLE_FREE_MESSAGES);
  const didEnableFreeMessages = didEnableFreeMessagesValue === true;
  const shouldSuggestFreeTest = !didEnableFreeMessages && messagesLimit > 0 && suggestTestIfEligible;

  const [selectedPricingOption, setSelectedPricingOption] = useState<string>(
    shouldSuggestFreeTest ? 'freeTest' : 'monthly',
  );

  const { run: purchasePackageOrSubscriptionOption } = useRequest(
    ({ pack, option }: { pack?: PurchasesPackage; option?: SubscriptionOption }) => {
      if (pack) {
        return Purchases.purchasePackage({ aPackage: pack });
      }
      if (option) {
        return Purchases.purchaseSubscriptionOption({ subscriptionOption: option });
      }
      throw new Error('No package or option provided');
    },
    {
      manual: true,
      onSuccess: result => {
        setPurchaseInProgress(false);
        // Grants access straight away, from what the SDK just told us.
        dispatch(userActions.setCurrentUserSubscription(result.customerInfo));
        setDisplayPage('paymentSucceeded');
        posthog.capture('paywall.purchase_succeeded', { purchased_item: result.productIdentifier });
        // And pull the user again so the backend's own verdict takes over. The
        // RevenueCat webhook is what turns this purchase into an access tier on
        // our side and it lags the purchase, hence the `until`.
        void refreshCurrentUser({ until: user => user.access_tier !== 'FREE' });
      },
      onError: (error: any) => {
        setPurchaseInProgress(false);
        setDisplayPage('paymentFailed');
        if (error.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
          // Purchase cancelled
          posthog.capture('paywall.purchase_cancelled');
        } else {
          // Error making purchase
          posthog.capture('paywall.purchase_failed');
        }
      },
    },
  );

  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeStatus, setPromoCodeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null);
  const [activeOffer, setActiveOffer] = useState<{
    package: PurchasesPackage;
    selectedOption: SubscriptionOption;
  } | null>(null); // For Android: the matched offer
  const [iosSyncingPurchases, setIosSyncingPurchases] = useState(false); // For iOS: track syncing state
  const appStateListenerRef = useRef<any>(null); // Track the listener for cleanup

  useEffect(() => {
    if (Platform.isNative()) {
      getOfferings();
    }
  }, []);

  // Cleanup app state listener when component unmounts or modal closes
  useEffect(() => {
    return () => {
      if (appStateListenerRef.current) {
        appStateListenerRef.current.remove();
        appStateListenerRef.current = null;
      }
    };
  }, []);

  // Set up app state listener when iOS promo code is active and modal is open
  useEffect(() => {
    if (isOpen && Platform.getPlatform() === 'ios' && iosSyncingPurchases && !appStateListenerRef.current) {
      const listener = App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          try {
            await Purchases.syncPurchases();
            // Get updated customer info after sync
            const result = await Purchases.getCustomerInfo();
            const customerInfo = result.customerInfo;
            // Check if user now has an active subscription
            const hasActiveSubscription = customerInfo.activeSubscriptions.length > 0;
            setIosSyncingPurchases(false); // Stop spinner
            if (hasActiveSubscription) {
              dispatch(userActions.setCurrentUserSubscription(customerInfo));
              void refreshCurrentUser({ until: user => user.access_tier !== 'FREE' });
              setDisplayPage('paymentSucceeded');
              posthog.capture('paywall.purchase_succeeded', {
                source: 'ios_promo_code',
                purchased_item: customerInfo.activeSubscriptions[0],
              });
            } else {
              setDisplayPage('paymentFailed');
              posthog.capture('paywall.purchase_failed', {
                source: 'ios_promo_code',
                reason: 'no_subscription_after_sync',
              });
            }
          } catch (error) {
            console.error('Error syncing purchases:', error);
            setIosSyncingPurchases(false); // Stop spinner
            setDisplayPage('paymentFailed');
            posthog.capture('paywall.purchase_failed', { source: 'ios_promo_code', reason: 'sync_error' });
          }
        }
      });
      appStateListenerRef.current = listener;
    }

    // Cleanup listener when modal closes or syncing stops
    if ((!isOpen || !iosSyncingPurchases) && appStateListenerRef.current) {
      appStateListenerRef.current.remove();
      appStateListenerRef.current = null;
    }
  }, [isOpen, iosSyncingPurchases, dispatch, refreshCurrentUser]);

  const handlePromoCodeSubmit = async () => {
    setPromoCodeStatus('loading');
    setPromoCodeError(null);
    if (Platform.getPlatform() === 'ios') {
      // iOS: Open App Store redeem link
      window.open(
        `https://apps.apple.com/redeem?ctx=offercodes&id=6503453272&code=${encodeURIComponent(promoCode)}`,
        '_blank',
      );
      setPromoCodeStatus('success');
      setIosSyncingPurchases(true); // Start spinner for iOS
      // The app state listener will be set up by the useEffect above
    } else if (Platform.getPlatform() === 'android') {
      // Android: Find offer with matching ID
      let found = false;
      if (offering) {
        // Search all packages in the offering for a matching offerId
        offering.availablePackages.forEach(pack => {
          if (pack.product && pack.product.subscriptionOptions) {
            const match = pack.product.subscriptionOptions.find(
              (option: SubscriptionOption) =>
                option.id &&
                option.id.toLowerCase().includes(':promo-') &&
                option.id.toLowerCase().endsWith(promoCode.toLowerCase()),
            );
            if (match) {
              setActiveOffer({ package: pack, selectedOption: match });
              setPromoCodeStatus('success');
              found = true;
            }
          }
        });
      }
      if (!found) {
        setPromoCodeStatus('error');
        setPromoCodeError(t('paywall.promoCode.invalid'));
        setActiveOffer(null);
      }
    } else {
      setPromoCodeStatus('error');
      setPromoCodeError(t('paywall.promoCode.unsupported'));
    }
    posthog.capture('paywall.promo_code_submitted', {
      promo_code: promoCode,
    });
  };

  const handlePurchase = () => {
    setPurchaseInProgress(true);
    if (selectedPricingOption === 'threeMonth') {
      if (Platform.getPlatform() === 'android' && activeOffer?.package.packageType === 'THREE_MONTH') {
        purchasePackageOrSubscriptionOption({ option: activeOffer.selectedOption });
        posthog.capture('paywall.purchase_started', {
          pricing_option: selectedPricingOption,
          price: offering?.threeMonth?.product.price,
          intro_price: activeOffer.selectedOption.pricingPhases[0]?.price.amountMicros / 1e6,
          currency: offering?.threeMonth?.product.currencyCode,
          promo_code: promoCode,
        });
      } else {
        purchasePackageOrSubscriptionOption({ pack: offering?.threeMonth as PurchasesPackage });
        posthog.capture('paywall.purchase_started', {
          pricing_option: selectedPricingOption,
          price: offering?.threeMonth?.product.price,
          intro_price: offering?.threeMonth?.product.introPrice?.price,
          currency: offering?.threeMonth?.product.currencyCode,
        });
      }
    } else if (selectedPricingOption === 'monthly') {
      if (Platform.getPlatform() === 'android' && activeOffer?.package.packageType === 'MONTHLY') {
        purchasePackageOrSubscriptionOption({ option: activeOffer.selectedOption });
        posthog.capture('paywall.purchase_started', {
          pricing_option: selectedPricingOption,
          price: offering?.monthly?.product.price,
          intro_price: activeOffer.selectedOption.pricingPhases[0]?.price.amountMicros / 1e6,
          currency: offering?.monthly?.product.currencyCode,
          promo_code: promoCode,
        });
      } else {
        purchasePackageOrSubscriptionOption({ pack: offering?.monthly as PurchasesPackage });
        posthog.capture('paywall.purchase_started', {
          pricing_option: selectedPricingOption,
          price: offering?.monthly?.product.price,
          intro_price: offering?.monthly?.product.introPrice?.price,
          currency: offering?.monthly?.product.currencyCode,
        });
      }
    } else {
      purchasePackageOrSubscriptionOption({ pack: offering?.weekly as PurchasesPackage });
      posthog.capture('paywall.purchase_started', {
        pricing_option: selectedPricingOption,
        price: offering?.weekly?.product.price,
      });
    }
  };

  const offersDescription = (
    <>
      <div className={styles.logoContainer}>
        <IconRound fill={colors.brand.accent} height={80} width={80} />
      </div>
      <h2 className={styles.title}>{t('paywall.title')}</h2>
      <div className={styles.advantages}>
        <p>{t('paywall.subtitle')}</p>
        <p className={styles.advantagesList}>
          <span>
            <CheckCircleFilled />
            {t('paywall.advantages.unlimitedMessages')}
          </span>
          <span>
            <CheckCircleFilled />
            {t('paywall.advantages.answersIn24h')}
            <span className={styles.exceptSundays}>&nbsp;*</span>
          </span>
        </p>
        <p className={styles.exceptSundays}>{t('chats.explainer.exceptSundays')}</p>
      </div>
      <CaregiverAvatarList direction="horizontal" />
      <Carousel autoplay>
        <div className={styles.testimonial}>
          <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled />
          <p>
            &quot;{t('paywall.testimonials.1')}&quot; - <span className={styles.testimonialAuthor}>Léa</span>
          </p>
        </div>
        <div className={styles.testimonial}>
          <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled />
          <p>
            &quot;{t('paywall.testimonials.2')}&quot; - <span className={styles.testimonialAuthor}>Camille</span>
          </p>
        </div>
        <div className={styles.testimonial}>
          <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled />
          <p>
            &quot;{t('paywall.testimonials.3')}&quot; - <span className={styles.testimonialAuthor}>Céline</span>
          </p>
        </div>
      </Carousel>
      <p className={styles.whyIsItNotFree}>
        <Collapse
          ghost
          expandIcon={() => <InfoCircleFilled />}
          items={[
            {
              key: '1',
              label: <>{t('paywall.whyPaywall')}</>,
              children: (
                <p>
                  <Trans i18nKey="paywall.explainWhyPaywall" components={{ br: <br /> }} />
                </p>
              ),
            },
          ]}
        />
      </p>
    </>
  );

  const webPaywallContent = (
    <>
      {offersDescription}
      <p className={styles.conditions}>
        <Trans
          i18nKey="paywall.webPaywall"
          components={{
            appstore: <Link target="_blank" to="https://apps.apple.com/fr/app/gynger/id6503453272" />,
            googleplay: <Link target="_blank" to="https://play.google.com/store/apps/details?id=com.gyngercare.app" />,
          }}
        />
      </p>
    </>
  );

  const offersContent = (
    <>
      {offersDescription}
      <div
        className={selectedPricingOption === 'oneWeek' ? styles.selectedPricingOption : styles.pricingOption}
        onClick={() => setSelectedPricingOption('oneWeek')}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            setSelectedPricingOption('oneWeek');
          }
        }}
      >
        <>
          <div>
            <p className={styles.pricingOptionTitle}>{t('paywall.oneWeekPrice.title')}</p>
            <p className={styles.pricingOptionSubtitle}>{t('paywall.oneWeekPrice.subtitle')}</p>
          </div>
          <p className={styles.pricingOptionPrice}>{offering?.weekly?.product.priceString}</p>
        </>
      </div>
      <div
        className={selectedPricingOption === 'monthly' ? styles.selectedPricingOption : styles.pricingOption}
        onClick={() => setSelectedPricingOption('monthly')}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            setSelectedPricingOption('monthly');
          }
        }}
      >
        {Platform.getPlatform() === 'android' &&
        activeOffer &&
        activeOffer.package.packageType === 'MONTHLY' &&
        activeOffer.selectedOption ? (
          <>
            <div>
              <p className={styles.pricingOptionTitle}>{t('paywall.monthlyPrice.title')}</p>
            </div>
            <p className={styles.pricingOptionPrice}>
              <b>{activeOffer.selectedOption.pricingPhases[0]?.price.formatted}</b> {t('paywall.perMonth')}
              <br />
              <div className={styles.secondPricePhase}>
                {t('paywall.monthlyPrice.secondPricePhase', {
                  months: activeOffer.selectedOption.pricingPhases[0].billingCycleCount,
                  priceString: activeOffer.package.product.priceString,
                })}
                <br />
                {t('paywall.threeMonthPrice.cancelAnytime')}
              </div>
            </p>
          </>
        ) : offering?.monthly?.product.introPrice ? (
          <>
            <div>
              <p className={styles.pricingOptionTitle}>{t('paywall.monthlyPrice.title')}</p>
              <p className={styles.pricingOptionSubtitle}>
                {t('paywall.monthlyPrice.offerPercent', {
                  percent: Math.floor(
                    (1 - offering?.monthly?.product.introPrice?.price / offering?.monthly?.product.price) * 100,
                  ),
                })}
              </p>
            </div>
            <p className={styles.pricingOptionPrice}>
              <b>{offering?.monthly?.product.introPrice?.priceString}</b> {t('paywall.perMonth')}
              <br />
              <div className={styles.secondPricePhase}>
                {t('paywall.monthlyPrice.secondPricePhase', {
                  months: offering?.monthly?.product.introPrice?.cycles,
                  priceString: offering?.monthly?.product.priceString,
                })}
                <br />
                {t('paywall.threeMonthPrice.cancelAnytime')}
              </div>
            </p>
          </>
        ) : (
          <>
            <p className={styles.pricingOptionTitle}>{t('paywall.monthlyPrice.title')}</p>
            <p className={styles.pricingOptionPrice}>
              {offering?.monthly?.product.priceString} {t('paywall.perMonth')}
            </p>
          </>
        )}
      </div>
      <div
        className={selectedPricingOption === 'threeMonth' ? styles.selectedPricingOption : styles.pricingOption}
        onClick={() => setSelectedPricingOption('threeMonth')}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            setSelectedPricingOption('threeMonth');
          }
        }}
      >
        {Platform.getPlatform() === 'android' &&
        activeOffer &&
        activeOffer.package.packageType === 'THREE_MONTH' &&
        activeOffer.selectedOption ? (
          <>
            <div>
              <p className={styles.pricingOptionTitle}>{t('paywall.threeMonthPrice.title')}</p>
            </div>
            <p className={styles.pricingOptionPrice}>
              <b>{activeOffer.selectedOption.pricingPhases[0]?.price.formatted}</b> {t('paywall.perQuarter')}
              <br />
              <div className={styles.secondPricePhase}>
                {t('paywall.threeMonthPrice.secondPricePhase', {
                  months: activeOffer.selectedOption.pricingPhases[0]?.billingCycleCount * 3,
                  priceString: activeOffer.package.product.priceString,
                })}
                <br />
                {t('paywall.threeMonthPrice.cancelAnytime')}
              </div>
            </p>
          </>
        ) : offering?.threeMonth?.product.introPrice ? (
          <>
            <div>
              <p className={styles.pricingOptionTitle}>{t('paywall.threeMonthPrice.title')}</p>
              <p className={styles.pricingOptionSubtitle}>
                {t('paywall.threeMonthPrice.offerPercent', {
                  percent: Math.floor(
                    (1 - offering?.threeMonth?.product.introPrice?.price / offering?.threeMonth?.product.price) * 100,
                  ),
                })}
              </p>
            </div>
            <p className={styles.pricingOptionPrice}>
              <b>{offering?.threeMonth?.product.introPrice?.priceString}</b> {t('paywall.perQuarter')}
              <br />
              <div className={styles.secondPricePhase}>
                {t('paywall.threeMonthPrice.secondPricePhase', {
                  months: offering?.threeMonth?.product.introPrice?.cycles * 3,
                  priceString: offering?.threeMonth?.product.priceString,
                })}
                <br />
                {t('paywall.threeMonthPrice.cancelAnytime')}
              </div>
            </p>
          </>
        ) : (
          <>
            <p className={styles.pricingOptionTitle}>{t('paywall.threeMonthPrice.title')}</p>
            <p className={styles.pricingOptionPrice}>
              {offering?.threeMonth?.product.priceString} {t('paywall.perQuarter')}
            </p>
          </>
        )}
      </div>
      {shouldSuggestFreeTest && (
        <Badge.Ribbon text={t('paywall.freeTest.ribbon')}>
          <div
            className={selectedPricingOption === 'freeTest' ? styles.selectedPricingOption : styles.pricingOption}
            onClick={() => setSelectedPricingOption('freeTest')}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                setSelectedPricingOption('oneWeek');
              }
            }}
          >
            <>
              <div>
                <p className={styles.pricingOptionTitle}>{t('paywall.freeTest.title')}</p>
                <p className={styles.pricingOptionSubtitle}>{t('paywall.freeTest.subtitle')}</p>
              </div>
            </>
          </div>
        </Badge.Ribbon>
      )}
      <p className={styles.conditions}>{t('paywall.conditions')}</p>
      {!showPromoInput && (
        <p className={styles.conditions}>
          <a style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setShowPromoInput(true)}>
            {t('paywall.promoCode.haveCode')}
          </a>
        </p>
      )}
      {showPromoInput && (
        <div className={styles.promoCodeContainer}>
          <Input
            placeholder={t('paywall.promoCode.placeholder')}
            value={promoCode}
            onChange={e => setPromoCode(e.target.value)}
            disabled={promoCodeStatus === 'loading' || iosSyncingPurchases}
            onPressEnter={handlePromoCodeSubmit}
          />
          <Button
            variant="primary"
            loading={promoCodeStatus === 'loading' || iosSyncingPurchases}
            onClick={handlePromoCodeSubmit}
          >
            {iosSyncingPurchases ? t('paywall.promoCode.syncing') : t('paywall.promoCode.submit')}
          </Button>
          <div className={styles.promoCodeStatus}>
            {promoCodeStatus === 'error' && promoCodeError && (
              <span style={{ color: colors.dataViz.veryBad }}>{promoCodeError}</span>
            )}
            {promoCodeStatus === 'success' && Platform.getPlatform() === 'ios' && !iosSyncingPurchases && (
              <span style={{ color: colors.brand.accentStrong }}>{t('paywall.promoCode.iosSuccess')}</span>
            )}
            {iosSyncingPurchases && Platform.getPlatform() === 'ios' && (
              <span style={{ color: colors.brand.accentStrong }}>{t('paywall.promoCode.iosSyncing')}</span>
            )}
            {promoCodeStatus === 'success' && Platform.getPlatform() === 'android' && (
              <span style={{ color: colors.brand.accentStrong }}>{t('paywall.promoCode.androidSuccess')}</span>
            )}
          </div>
        </div>
      )}
      <p className={styles.conditions}>
        <Trans i18nKey="paywall.doYouHaveACode" components={{ a: <Link to="/settings" /> }} />
      </p>
      <Button
        variant="primary"
        size="lg"
        block
        onClick={() => {
          if (selectedPricingOption === 'freeTest') {
            onFreeMessagesEnabled?.();
            setDisplayPage('freeMessagesEnabled');
            posthog.capture('paywall.free_messages_enabled');
          } else {
            handlePurchase();
          }
        }}
        loading={purchaseInProgress || iosSyncingPurchases}
      >
        {selectedPricingOption === 'oneWeek'
          ? t('paywall.purchaseButtonOneWeek')
          : selectedPricingOption === 'freeTest'
            ? t('paywall.freeMessagesButton.title')
            : t('paywall.purchaseButton')}
      </Button>
      <p className={styles.legal}>
        <Link target="_blank" to="https://gyngercare.com/mentionslegales/">
          {t('paywall.termsOfUse')}
        </Link>{' '}
        -{' '}
        <Link target="_blank" to="https://gyngercare.com/mentionslegales/">
          {t('paywall.privacyPolicy')}
        </Link>
      </p>
    </>
  );

  const paymentSucceededContent = (
    <>
      <h2 className={styles.paymentSuccessTitle}>
        {t('paywall.paymentSucceeded.title')} <CheckCircleFilled />
      </h2>
      <p className={styles.paymentDescription}>{t('paywall.paymentSucceeded.description')}</p>

      <Button
        variant="primary"
        size="lg"
        block
        onClick={() => {
          closeModal();
        }}
      >
        {t('paywall.paymentSucceeded.callToAction')}
      </Button>
    </>
  );

  const paymentFailedContent = (
    <>
      <h2 className={styles.paymentFailedTitle}>
        <CloseCircleOutlined /> {t('paywall.paymentFailed.title')}
      </h2>
      <p className={styles.paymentDescription}>{t('paywall.paymentFailed.description')}</p>
      <p className={styles.paymentDescription}>
        <Trans
          i18nKey="paywall.paymentFailed.needHelp"
          components={{ a: <Link to={`mailto:${t('global.support.supportEmail')}`} /> }}
        />
      </p>
      <Button variant="primary" size="lg" block onClick={() => setDisplayPage('offers')}>
        {t('paywall.paymentFailed.callToAction')}
      </Button>
    </>
  );

  const freeMessagesEnabledContent = (
    <>
      <h2 className={styles.paymentSuccessTitle}>
        {t('paywall.freeMessagesEnabled.title')} <CheckCircleFilled />
      </h2>
      <p className={styles.paymentDescription}>
        {t('paywall.freeMessagesEnabled.description', { count: messagesLimit })}
      </p>

      <Button
        variant="primary"
        size="lg"
        block
        onClick={() => {
          closeModal();
        }}
      >
        {t('paywall.freeMessagesEnabled.callToAction')}
      </Button>
    </>
  );

  return (
    <Modal
      open={isOpen}
      afterOpenChange={open => {
        if (open) {
          posthog.capture('paywall.prices_displayed', {
            monthly_price: offering?.monthly?.product.price,
            monthly_intro_price: offering?.monthly?.product.introPrice?.price,
            three_month_price: offering?.threeMonth?.product.price,
            three_month_intro_price: offering?.threeMonth?.product.introPrice?.price,
            currency: offering?.monthly?.product.currencyCode,
          });
        }
      }}
      onCancel={() => {
        onClose();
        posthog.capture('paywall.closed');
      }}
      footer={null}
    >
      {displayPage === 'offers' && <>{Platform.getPlatform() === 'web' ? webPaywallContent : offersContent}</>}
      {displayPage === 'paymentSucceeded' && paymentSucceededContent}
      {displayPage === 'paymentFailed' && paymentFailedContent}
      {displayPage === 'freeMessagesEnabled' && freeMessagesEnabledContent}
    </Modal>
  );
};

export default PaywallModal;
